import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent } from "@bowfast/ui/components/card";
import { Skeleton } from "@bowfast/ui/components/skeleton";
import { cn } from "@bowfast/ui/lib/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AnchorIcon, CompassIcon, PlusIcon, SailboatIcon, SearchIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { ListLoadError } from "~/components/shared/ListLoadError";
import { ListSkeleton } from "~/components/shared/ListSkeleton";
import { VesselCard } from "~/components/vessels/VesselCard";
import { VesselsAtSeaSpotlight } from "~/components/vessels/VesselsAtSeaSpotlight";
import { VesselStatus } from "~/graphql/generated";
import { fadeUp, stagger } from "~/lib/animations";
import { createSdk } from "~/lib/gql";
import { useOrg } from "~/lib/org-context";
import { TAB_LABELS } from "./vessels.config";
import { TAB_FILTERS, type TabFilter } from "./vessels.types";
import { activeExpeditionName } from "./vessels.utils";

export function VesselsListPage() {
  const auth = useAuth();
  const { orgId } = useOrg();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<TabFilter>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggle = useCallback((id: string) => setExpandedId((prev) => (prev === id ? null : id)), []);

  const { data, isPending, isError } = useQuery({
    queryKey: ["vessels-list", orgId],
    queryFn: () => createSdk(auth.user?.access_token ?? "").VesselsList({ orgId }),
    enabled: !!auth.user?.access_token && !!orgId,
    staleTime: 60 * 1000,
  });

  const vessels = useMemo(() => data?.vessels ?? [], [data]);

  const filtered = useMemo(() => {
    let list = vessels;
    if (activeTab === "active") list = list.filter((v) => v.status === VesselStatus.Active);
    else if (activeTab === "under-maintenance") list = list.filter((v) => v.status === VesselStatus.UnderMaintenance);
    else if (activeTab === "retired") list = list.filter((v) => v.status === VesselStatus.Retired);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          v.make?.toLowerCase().includes(q) ||
          v.model?.toLowerCase().includes(q) ||
          v.homePort?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [vessels, activeTab, search]);

  return (
    <div className="mx-auto w-full max-w-350 space-y-5 p-4 md:p-6">
      {/* Page header */}
      <motion.div {...fadeUp} className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex size-11 items-center justify-center rounded-xl bg-abyss-50 dark:bg-abyss-800">
            <AnchorIcon className="size-6 text-abyss-500" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight md:text-[1.375rem]">Your fleet</h1>
            <p className="text-sm text-muted-foreground">
              {isPending
                ? "Loading…"
                : `${vessels.length} vessels · ${vessels.filter((v) => v.status === VesselStatus.Active).length} active`}
            </p>
          </div>
        </div>
        <Button size="sm" className="gap-2" nativeButton={false} render={<Link to="/vessels/new" />}>
          <PlusIcon className="size-3.5" />
          Add vessel
        </Button>
      </motion.div>

      {/* Metrics strip */}
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.04 }}>
        <div className="flex flex-wrap gap-2">
          {isPending
            ? (["pill-a", "pill-b", "pill-c"] as const).map((key) => (
                <Skeleton key={key} className="h-9 w-24 rounded-lg" />
              ))
            : (
                [
                  { label: "Total", value: String(vessels.length), Icon: AnchorIcon, color: "text-abyss-500" },
                  {
                    label: "Active",
                    value: String(vessels.filter((v) => v.status === VesselStatus.Active).length),
                    Icon: SailboatIcon,
                    color: "text-shallow-500",
                  },
                  {
                    label: "At sea",
                    value: String(vessels.filter((v) => activeExpeditionName(v) !== undefined).length),
                    Icon: CompassIcon,
                    color: "text-shallow-600",
                  },
                ] as const
              ).map((m) => (
                <div
                  key={m.label}
                  className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2"
                >
                  <m.Icon className={cn("size-3.5", m.color)} />
                  <span className="font-heading text-sm font-bold tabular-nums">{m.value}</span>
                  <span className="text-xs text-muted-foreground">{m.label}</span>
                </div>
              ))}
        </div>
      </motion.div>

      {/* Vessels at sea spotlight */}
      {!isPending && (
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.06 }}>
          <VesselsAtSeaSpotlight vessels={vessels} />
        </motion.div>
      )}

      {/* Toolbar: tabs + search */}
      <motion.div
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.08 }}
        className="flex flex-wrap items-center gap-3"
      >
        <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
          {TAB_FILTERS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={cn(
                "relative rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                activeTab === tab ? "text-white" : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              {activeTab === tab && (
                <motion.div
                  layoutId="vessel-tab-bg"
                  className="absolute inset-0 rounded-md bg-abyss-500"
                  transition={{ type: "spring", stiffness: 500, damping: 35 }}
                />
              )}
              <span className="relative z-10">{TAB_LABELS[tab]}</span>
            </button>
          ))}
        </div>

        <div className="flex-1" />

        <div className="relative">
          <SearchIcon className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            placeholder="Search vessels…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-48 rounded-lg border border-input bg-card pl-8 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20"
          />
        </div>
      </motion.div>

      {/* Error state */}
      {isError && (
        <ListLoadError
          title="Couldn't load your fleet"
          onRetry={() => queryClient.invalidateQueries({ queryKey: ["vessels-list", orgId] })}
        />
      )}

      {/* Loading state */}
      {isPending && <ListSkeleton rows={4} />}

      {/* Vessel cards */}
      {!isPending && !isError && (
        <motion.div className="space-y-3" initial="initial" animate="animate" variants={stagger}>
          {filtered.map((vessel) => (
            <motion.div key={vessel.id} variants={fadeUp}>
              <VesselCard vessel={vessel} isExpanded={expandedId === vessel.id} onToggle={() => toggle(vessel.id)} />
            </motion.div>
          ))}
          {filtered.length === 0 && (
            <motion.div variants={fadeUp}>
              <Card className="py-12 text-center">
                <CardContent>
                  <SailboatIcon className="mx-auto size-10 text-muted-foreground/30" />
                  <p className="mt-3 font-heading text-base font-semibold">No vessels found</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {search ? "Try a different search term" : "No vessels match this filter"}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </motion.div>
      )}
    </div>
  );
}
